import { StackedBars } from '../charts/StackedBars.js'
import { BAD, GOLD, MARINE, SEQUENTIAL } from '../charts/chartColors.js'
import { FILL_LABELS, FILL_ORDER, latestByFacility, spanTotals, sumByPeriod } from '../insightsSeries.js'
import { monthLabel } from '../labels.js'
import type { FacilityMonthRollup, PortfolioRow } from '../types.js'

const FILL_COLORS: Record<(typeof FILL_ORDER)[number], string> = { roster: '#08404F', offer: '#0C5E74', firstCome: '#4F93A8', censusAdd: '#9CC5D2', bridge: '#C9D4DA', assigned: GOLD }

/**
 * Coverage (plan section 6.5): who filled each released month's seats and what ran short, the fill at release per
 * facility against its minimum, the board's traffic by month, and the rules the runs strained per facility.
 */
export function CoverageScreen({ rollups, periods, rows }: { rollups: readonly FacilityMonthRollup[]; periods: readonly string[]; rows: readonly PortfolioRow[] }) {
  const released = rollups.filter((rollup) => rollup.released)
  const fills = [
    ...FILL_ORDER.map((key) => ({ key, name: FILL_LABELS[key], color: FILL_COLORS[key], values: sumByPeriod(released, periods, (rollup) => rollup.coverage.fills[key]) })),
    { key: 'ranShort', name: 'Ran short', color: BAD, values: sumByPeriod(released, periods, (rollup) => rollup.coverage.ranShort) },
  ]
  const traffic = [
    { key: 'swaps', name: 'Swaps completed', color: MARINE, values: sumByPeriod(released, periods, (rollup) => rollup.coverage.swapsCompleted) },
    { key: 'vacated', name: 'Vacated', color: '#8E7320', values: sumByPeriod(released, periods, (rollup) => rollup.coverage.vacated) },
    { key: 'daysOff', name: 'Days off taken by the run', color: '#7B5AA6', values: sumByPeriod(released, periods, (rollup) => rollup.coverage.daysOffTaken) },
    { key: 'flex', name: 'Flexed, floated, recalled', color: '#2F6FB5', values: sumByPeriod(released, periods, (rollup) => rollup.coverage.flexedOff + rollup.coverage.floated + rollup.coverage.recalled) },
  ]
  const latest = latestByFacility(released)
  const bullets = rows
    .map((row) => ({ row, rollup: latest.get(row.facilityId) ?? null }))
    .filter((entry): entry is { row: PortfolioRow; rollup: FacilityMonthRollup } => entry.rollup !== null && entry.rollup.coverage.filledPct !== null)
    .sort((a, b) => (b.rollup.coverage.filledPct ?? 0) - (a.rollup.coverage.filledPct ?? 0))
  const strain = rows
    .map((row) => ({ row, ...spanTotals(rollups.filter((rollup) => rollup.facilityId === row.facilityId)) }))
    .map((entry) => ({ ...entry, overtimePer100: entry.headcount > 0 ? Math.round((entry.overtimeFlags / entry.headcount) * 1000) / 10 : null, daysPer100: entry.headcount > 0 ? Math.round((entry.daysInARowFlags / entry.headcount) * 1000) / 10 : null }))
    .sort((a, b) => (b.overtimePer100 ?? -1) - (a.overtimePer100 ?? -1))
  const overtimeValues = strain.map((entry) => entry.overtimePer100).filter((value): value is number => value !== null).sort((a, b) => a - b)
  const median = overtimeValues.length === 0 ? null : overtimeValues[Math.floor(overtimeValues.length / 2)] ?? null
  const maxStrain = Math.max(1, ...strain.map((entry) => Math.max(entry.overtimePer100 ?? 0, entry.daysPer100 ?? 0)))

  if (released.length === 0) {
    return <div className="rounded-lg border border-marine/15 bg-white px-4 py-8 text-center text-xs text-ink-mid">No month in this span has been released yet. Coverage is read from a released month and its board.</div>
  }
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex-1 text-sm font-black text-ink">Who filled the month</h2>
          <span className="text-[11px] text-ink-mid">Share of each released month's filled seats by who filled them, ran short apart</span>
        </div>
        <StackedBars periods={periods} layers={fills} normalized periodLabel={monthLabel} format={(value) => String(Math.round(value))} title="Share of each month's seats by who filled them" height={280} />
        <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
          {fills.map((layer) => (
            <span key={layer.key} className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm" style={{ background: layer.color }} />
              {layer.name}
            </span>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Fill at release</h2>
            <span className="text-[11px] text-ink-mid">Each facility's latest released month, against its minimum</span>
          </div>
          <ul className="flex flex-col gap-1.5">
            {bullets.map(({ row, rollup }) => {
              const pct = rollup.coverage.filledPct ?? 0
              const min = rollup.coverage.minFilledPct
              return (
                <li key={row.facilityId} className="flex items-center gap-3 text-[11px]">
                  <span className="w-32 truncate text-ink" title={row.name}>
                    {row.name}
                  </span>
                  <span className="relative h-4 flex-1 rounded bg-silver-lt" role="img" aria-label={`${row.name} ${pct}% filled at release${min === null ? '' : `, minimum ${min}%`}`}>
                    <span className="absolute inset-y-1 left-0 rounded" style={{ width: `${Math.min(100, pct)}%`, background: min !== null && pct < min ? BAD : pct >= 80 ? MARINE : GOLD }} />
                    {min !== null && <span className="absolute inset-y-0 w-0.5 bg-ink" style={{ left: `${Math.min(100, min)}%` }} title={`Minimum ${min}%`} />}
                  </span>
                  <span className="w-20 text-right text-ink-mid">
                    <span className="font-bold text-ink">{Math.round(pct)}%</span> {monthLabel(rollup.period)}
                  </span>
                </li>
              )
            })}
            {bullets.length === 0 && <li className="text-[11px] text-ink-mid">No released month carries a fill yet.</li>}
          </ul>
          <div className="text-[10px] text-ink-mid">The black tick is the facility's minimum; a bar in coral is below it.</div>
        </section>
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">The board's traffic</h2>
            <span className="text-[11px] text-ink-mid">Per month, from the ledger</span>
          </div>
          <StackedBars periods={periods} layers={traffic} periodLabel={monthLabel} format={(value) => String(Math.round(value))} title="Board events per month" height={240} />
          <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
            {traffic.map((layer) => (
              <span key={layer.key} className="inline-flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-sm" style={{ background: layer.color }} />
                {layer.name}
              </span>
            ))}
          </div>
        </section>
      </div>

      <section className="flex flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex-1 text-sm font-black text-ink">Rules under strain</h2>
          <span className="text-[11px] text-ink-mid">Placements the runs flagged over the span, per 100 workers · weekly overtime and days in a row</span>
        </div>
        <ul className="flex flex-col gap-1.5">
          {strain.map((entry) => (
            <li key={entry.row.facilityId} className="flex items-center gap-3 text-[11px]">
              <span className="w-32 truncate text-ink" title={entry.row.name}>
                {entry.row.name}
              </span>
              <span className="flex flex-1 flex-col gap-0.5" role="img" aria-label={`${entry.row.name}: ${entry.overtimePer100 ?? 0} overtime reviews and ${entry.daysPer100 ?? 0} days-in-a-row flags per 100 workers`}>
                <span className="h-2 rounded" style={{ width: `${((entry.overtimePer100 ?? 0) / maxStrain) * 100}%`, background: median !== null && (entry.overtimePer100 ?? 0) > median * 2 ? BAD : MARINE }} />
                <span className="h-2 rounded" style={{ width: `${((entry.daysPer100 ?? 0) / maxStrain) * 100}%`, background: SEQUENTIAL[1] }} />
              </span>
              <span className="w-24 text-right text-ink-mid">
                <span className="font-bold text-ink">{entry.overtimePer100 ?? '—'}</span> · {entry.daysPer100 ?? '—'}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3 text-[10px] text-ink-mid">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-3 rounded-sm" style={{ background: MARINE }} /> Weekly overtime reviews
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-3 rounded-sm" style={{ background: SEQUENTIAL[1] }} /> Days in a row
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-3 rounded-sm" style={{ background: BAD }} /> Overtime past twice the median{median !== null ? ` (${median})` : ''}
          </span>
        </div>
      </section>
    </div>
  )
}
